import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AppContextFactory } from '../../../00_modules/contextbar/services/app-context/app-context.factory';
import { NavigationResolver } from '../../../00_modules/contextbar/services/navigation/resolver/navigation.resolver';
import { catchError, switchMap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, from, of } from 'rxjs';
import { FormatService } from '../../../00_modules/core/services/format.service';
import { OrderInformationService } from '../../../00_modules/core/services/order-information.service';
import { SplitButtonComponent } from '../../button/split-button/split-button.component';
import { OrderInformation } from '../../../00_modules/core/models/OrderInformation';
import { ContentHostingContext } from '../../../00_modules/contextbar/services/app-context';
import { Buttons } from '../../../00_modules/contextbar/services/action-buttons/models/buttons.model';
import { ActionButtonGroupResolver } from '../../../00_modules/contextbar/services/action-buttons/resolver/action-button-group.resolver';
import { Router } from '@angular/router';
import { ContextBarFacade } from '../../../store/facade/context-bar.facade';
import { OrderingFacade } from '../../../store/facade/ordering.facade';
import { UiFacade } from '../../../store/facade/ui.facade';
import { Title } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProductConfigurationFacade } from '../../../store/facade/product-configuration.facade';
import { OrderLinkDialogComponent } from '../../../domain/ordering/dialogs/order-link-dialog/order-link-dialog.component';
import { OrderLinkDialogConfig } from '../../../domain/ordering/dialogs/order-link-dialog/order-link-dialog.config';
import { IconComponent } from '../../../00_modules/shared/components/fa-icon/fa-icon.component';
import { UserFacade } from '../../../store/facade/user.facade';
import { DialogConfig, JeDialogService } from '../../../00_modules/shared/components/dialog/dialog.service';
import { ButtonComponent } from '../../../00_modules/shared/components/buttons/button/button.component';
import { JeTooltipDirective } from '../../../00_modules/shared/components/tooltip/tooltip.directive';

@Component({
    selector: 'je-context-header',
    imports: [SplitButtonComponent, JeTooltipDirective, IconComponent, TranslateModule, ButtonComponent],
    templateUrl: './context-header.component.html',
})
export class ContextHeaderComponent implements OnInit {
    public orderId: number;
    public orderInformation: OrderInformation;
    public orderState: string;
    public showHeadline: boolean = false;
    public showOrderInformation: boolean = false;
    public showProductInformation: boolean = false;
    public productInformation: any;
    public buttons: Buttons;
    public headlineTranslationKey: string = null;
    public hostingContext: ContentHostingContext;
    public appContext$ = this.appContextFactory.context$;
    public isPrimaryButtonDisabled = false;
    public showOrderInformationHostingContexts = [
        ContentHostingContext.Order,
        ContentHostingContext.ReadonlyOrder,
        ContentHostingContext.InvoiceSimulation,
        ContentHostingContext.QuestionnaireReadonly,
        ContentHostingContext.Questionnaire,
    ];
    public showProductInformationHostingContexts = [
        ContentHostingContext.MasterDataProductConfiguration,
        ContentHostingContext.MasterDataProductConfigurationSettings,
        ContentHostingContext.MasterDataProductConfigurationFields,
        ContentHostingContext.MasterDataProductConfigurationHintTexts,
        ContentHostingContext.MasterDataProductConfigurationAccounting,
        ContentHostingContext.MasterDataProductConfigurationProductNotifications,
        ContentHostingContext.MasterDataProductConfigurationQualityAssurance,
        ContentHostingContext.MasterDataProductConfigurationCheckRules,
        ContentHostingContext.MasterDataProductConfigurationCheckTextBlocks,
    ];
    public isTeamOrder = false;
    public userId: number;
    public roleName: string;
    private readonly TITLE = 'OKAPI';
    private readonly destroyRef = inject(DestroyRef);
    private readonly orderStateId$ = new BehaviorSubject<number>(null);

    constructor(
        private orderInformationService: OrderInformationService,
        private formatService: FormatService,
        private buttonGroupResolver: ActionButtonGroupResolver,
        private appContextFactory: AppContextFactory,
        private navigationResolver: NavigationResolver,
        private router: Router,
        private contextBarFacade: ContextBarFacade,
        private orderingFacade: OrderingFacade,
        private uiFacade: UiFacade,
        private titleService: Title,
        private translateService: TranslateService,
        private productConfigurationFacade: ProductConfigurationFacade,
        private dialog: JeDialogService,
        private userFacade: UserFacade,
        private jeDialogService: JeDialogService,
    ) {}

    async ngOnInit(): Promise<void> {
        if (!this.appContext$) {
            return;
        } else {
            this.userFacade.userId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
                this.userId = id;
            });
            this.userFacade.currentRole$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
                this.roleName = role?.name;
            });
            this.productConfigurationFacade.productConfiguration$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(product => {
                this.productInformation = {
                    customerName: product?.customerName,
                    version: product?.versions?.find(v => v.isCurrent)?.version,
                    productName: product?.basicSettings.name,
                };
            });

            this.appContext$
                .pipe(
                    switchMap(context =>
                        this.orderStateId$.pipe(
                            switchMap(orderStateId =>
                                from(this.buttonGroupResolver.getActionButtons(context, orderStateId)).pipe(
                                    catchError(err => {
                                        console.error('Failed to resolve action buttons', err);
                                        return of(null);
                                    }),
                                ),
                            ),
                        ),
                    ),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(buttonGroup => {
                    this.buttons = buttonGroup;
                });

            this.appContext$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(p => {
                this.orderId = p.orderId;
                this.hostingContext = p.hostingContext;

                if (this.showOrderInformationHostingContexts.includes(this.hostingContext)) {
                    this.showHeadline = false;
                    this.showOrderInformation = true;
                    this.showProductInformation = false;
                } else if (this.showProductInformationHostingContexts.includes(this.hostingContext)) {
                    this.showHeadline = false;
                    this.showOrderInformation = false;
                    this.showProductInformation = true;
                } else {
                    this.showHeadline = true;
                    this.showOrderInformation = false;
                    this.showProductInformation = false;
                }
            });

            combineLatest([this.appContext$, this.orderingFacade.order$])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(([context]) => {
                    if (this.showOrderInformationHostingContexts.includes(context.hostingContext)) {
                        this.updateOrderingInformation();
                    } else {
                        this.orderStateId$.next(null);
                    }
                });
        }

        this.contextBarFacade.selectedKey$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(selectedKey => {
            this.appContext$.pipe(switchMap(context => from(this.navigationResolver.getNavigation(context)))).subscribe(navigation => {
                let selectedItem = navigation?.find(item => item.key === selectedKey);
                if (selectedItem) {
                    this.headlineTranslationKey = selectedItem?.label;
                } else if (navigation) {
                    let url = this.router.url.split('/');
                    navigation?.forEach(item => {
                        if (item.route[item.route.length - 1] === url[url.length - 1]) {
                            this.headlineTranslationKey = item.label;
                        }
                    });
                } else if (this.hostingContext === ContentHostingContext.InvoiceSimulation) {
                    this.headlineTranslationKey = 'ACCOUNTING.INVOICE_SIMULATION';
                } else {
                    this.headlineTranslationKey = '';
                }
                if (!this.showOrderInformationHostingContexts.includes(this.hostingContext)) {
                    let headline = this.headlineTranslationKey ? this.translateService.instant(this.headlineTranslationKey) : '';
                    let completeTitle = [this.TITLE, headline].filter(v => v !== '').join(' - ');
                    this.titleService.setTitle(completeTitle);
                }
            });
        });

        this.uiFacade.isDirty$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isDirty => {
            this.isPrimaryButtonDisabled = isDirty;
        });
    }

    public onOrderLinkClicked() {
        this.dialog.open(OrderLinkDialogComponent, <DialogConfig>{
            data: <OrderLinkDialogConfig>{
                orderId: this.orderId,
            },
        });
    }

    private updateButtons(orderStateId: number) {
        this.orderStateId$.next(orderStateId);
    }

    private updateOrderingInformation() {
        this.orderInformationService
            .getOrderInformation(this.orderId)
            .then(oi => {
                this.orderInformation = oi;
                this.orderState = this.formatService.getOrderStateAsString(oi.orderState);
                this.updateButtons(oi.orderStateId);

                this.uiFacade.isTeamOrder$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isTeamOrder => {
                    this.isTeamOrder = isTeamOrder;
                });

                if (this.showOrderInformationHostingContexts.includes(this.hostingContext)) {
                    let completeTitle = [this.TITLE, this.orderId, oi.licensePlate, oi.vehicleIdentificationNumber]
                        .filter(v => v !== '' && v !== null && v !== undefined)
                        .join(' - ');
                    this.titleService.setTitle(completeTitle);
                }
            })
            .catch(err => {
                console.error('Failed to load order information for the context header', err);
            });
    }
}
